import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { WFirmaVatCode, wfirmaVatCodeService } from '../services/wfirmaVatCodeService';

interface SendInvoiceToWFirmaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (forceResync?: boolean) => void;
  onSyncCounterparty: () => void;
  invoiceNumber: string;
  invoiceWfirmaId?: number;
  counterpartyName: string;
  counterpartyWfirmaId?: number;
  companyId: string;
  needsCounterpartySync: boolean;
  status: 'confirm' | 'syncing' | 'success' | 'error';
  errorMessage?: string;
  missingVatCodes?: Array<{ itemIndex: number; itemName: string; rate: number; isEu: boolean }>;
  onVatCodeSelected?: (itemIndex: number, vatCodeId: number) => void;
}

const SendInvoiceToWFirmaModal: React.FC<SendInvoiceToWFirmaModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onSyncCounterparty,
  invoiceNumber,
  invoiceWfirmaId,
  counterpartyName,
  counterpartyWfirmaId,
  companyId,
  needsCounterpartySync,
  status,
  errorMessage,
  missingVatCodes = [],
  onVatCodeSelected
}) => {
  const { t } = useTranslation();
  const [vatCodes, setVatCodes] = useState<WFirmaVatCode[]>([]);
  const [selectedVatCodes, setSelectedVatCodes] = useState<Map<number, number>>(new Map());
  const [loadingVatCodes, setLoadingVatCodes] = useState(false);

  useEffect(() => {
    if (isOpen && missingVatCodes.length > 0) {
      loadVatCodes();
    }
  }, [isOpen, missingVatCodes.length]);

  const loadVatCodes = async () => {
    setLoadingVatCodes(true);
    try {
      const codes = await wfirmaVatCodeService.getVatCodes(companyId);
      setVatCodes(codes);
    } catch (error) {
      console.error('Error loading VAT codes:', error);
    } finally {
      setLoadingVatCodes(false);
    }
  };

  const handleVatCodeSelect = (itemIndex: number, vatCodeId: number) => {
    setSelectedVatCodes(prev => {
      const newMap = new Map(prev);
      newMap.set(itemIndex, vatCodeId);
      return newMap;
    });
    if (onVatCodeSelected) {
      onVatCodeSelected(itemIndex, vatCodeId);
    }
  };

  const handleConfirm = () => {
    // Проверяем, что все VAT коды выбраны
    if (missingVatCodes.length > 0) {
      const allSelected = missingVatCodes.every(item => selectedVatCodes.has(item.itemIndex));
      if (!allSelected) {
        return; // Не подтверждаем, если не все коды выбраны
      }
    }
    onConfirm();
  };

  if (!isOpen) return null;

  const renderContent = () => {
    switch (status) {
      case 'confirm':
        if (needsCounterpartySync) {
          return (
            <>
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                {t('invoices.wfirma.syncCounterpartyRequired') || 'Требуется синхронизация контрагента'}
              </h3>
              
              <p className="text-sm text-gray-600 text-center mb-6">
                {t('invoices.wfirma.syncCounterpartyMessage') || 
                  `Контрагент "${counterpartyName}" не синхронизирован с wFirma. Необходимо синхронизировать контрагента перед отправкой фактуры.`}
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSyncCounterparty}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('invoices.wfirma.syncCounterparty') || 'Синхронизировать контрагента'}
                </button>
              </div>
            </>
          );
        }

        if (missingVatCodes.length > 0) {
          return (
            <>
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                {t('invoices.wfirma.selectVatCodes') || 'Выберите VAT коды'}
              </h3>
              
              <p className="text-sm text-gray-600 text-center mb-4">
                {t('invoices.wfirma.selectVatCodesMessage') || 
                  'Для некоторых позиций не найдены VAT коды. Пожалуйста, выберите подходящие коды:'}
              </p>

              <div className="max-h-64 overflow-y-auto mb-6 space-y-3">
                {missingVatCodes.map((item) => {
                  const filteredCodes = vatCodes.filter(code => 
                    Math.abs(code.rate - item.rate) < 0.01 && code.isEu === item.isEu
                  );
                  const selectedId = selectedVatCodes.get(item.itemIndex);
                  
                  return (
                    <div key={item.itemIndex} className="border border-gray-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {item.itemName} ({item.rate}%{item.isEu ? ', ЕС' : ''})
                      </p>
                      {loadingVatCodes ? (
                        <div className="text-sm text-gray-500">Загрузка кодов...</div>
                      ) : (
                        <select
                          value={selectedId || ''}
                          onChange={(e) => handleVatCodeSelect(item.itemIndex, Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Выберите VAT код</option>
                          {filteredCodes.map((code) => (
                            <option key={code.id} value={code.wfirmaId}>
                              {code.name || code.code} ({code.rate}%)
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={missingVatCodes.some(item => !selectedVatCodes.has(item.itemIndex))}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.confirm') || 'Подтвердить'}
                </button>
              </div>
            </>
          );
        }

        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('invoices.wfirma.sendConfirmTitle') || 'Отправка фактуры в wFirma'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {(t('invoices.wfirma.sendConfirm') || 'Отправить фактуру "{number}" в wFirma?').replace('{number}', invoiceNumber)}
            </p>

            {invoiceWfirmaId && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                {t('invoices.wfirma.alreadySyncedPrompt') ||
                  'Фактура уже была отправлена в wFirma. Отправить как новую фактуру?'}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              {invoiceWfirmaId ? (
                <>
                  <button
                    type="button"
                    onClick={() => onConfirm(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {t('invoices.wfirma.sendExistingLink') || 'Отправить без создания новой'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onConfirm(true)}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    {t('invoices.wfirma.sendAsNew') || 'Отправить как новую'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('common.confirm') || 'Подтвердить'}
                </button>
              )}
            </div>
          </>
        );

      case 'syncing':
        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('invoices.wfirma.syncing') || 'Отправка в wFirma...'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {t('invoices.wfirma.syncingMessage') || 'Пожалуйста, подождите, идет отправка фактуры в wFirma'}
            </p>
          </>
        );

      case 'success':
        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('invoices.wfirma.sendSuccessTitle') || 'Фактура отправлена'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {t('invoices.wfirma.sendSuccess') || 'Фактура успешно отправлена в wFirma'}
            </p>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('common.close') || 'Закрыть'}
              </button>
            </div>
          </>
        );

      case 'error':
        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('invoices.wfirma.sendErrorTitle') || 'Ошибка отправки'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {errorMessage || t('invoices.wfirma.sendError') || 'Произошла ошибка при отправке фактуры в wFirma'}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.close') || 'Закрыть'}
              </button>
            </div>
          </>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SendInvoiceToWFirmaModal;
